// SPDX-License-Identifier: AGPL-3.0-or-later

import {MAX_FOLLOWERS_PER_ANNOUNCEMENT_CHANNEL} from '@fluxer/constants/src/AnnouncementConstants';
import type {LimitKey} from '@fluxer/constants/src/LimitConfigMetadata';
import {
	ATTACHMENT_MAX_SIZE_NON_PREMIUM,
	ATTACHMENT_MAX_SIZE_PREMIUM,
	AVATAR_MAX_SIZE,
	EMOJI_MAX_SIZE,
	FEED_IMAGE_DEFAULT_MAX_BYTES,
	FEED_IMAGE_HARD_MAX_BYTES,
	FEED_POSTS_PER_DAY_HARD_MAX,
	FEED_VIDEO_BYTES_PER_DAY_HARD_MAX,
	FEED_VIDEO_CREATOR_MAX_DURATION_SECONDS,
	FEED_VIDEO_DEFAULT_MAX_BYTES,
	FEED_VIDEO_DEFAULT_MAX_DURATION_SECONDS,
	FEED_VIDEO_HARD_MAX_BYTES,
	MAX_ATTACHMENTS_PER_MESSAGE,
	MAX_BIO_LENGTH,
	MAX_BOOKMARKS_NON_PREMIUM,
	MAX_BOOKMARKS_PREMIUM,
	MAX_CHANNELS_PER_CATEGORY,
	MAX_EMBEDS_PER_MESSAGE,
	MAX_FAVORITE_MEME_TAGS,
	MAX_FAVORITE_MEMES_NON_PREMIUM,
	MAX_FAVORITE_MEMES_PREMIUM,
	MAX_GROUP_DM_RECIPIENTS,
	MAX_GROUP_DMS_PER_USER,
	MAX_GUILD_CHANNELS,
	MAX_GUILD_EMOJIS,
	MAX_GUILD_EMOJIS_ANIMATED,
	MAX_GUILD_EMOJIS_ANIMATED_MORE_EMOJI,
	MAX_GUILD_EMOJIS_STATIC,
	MAX_GUILD_EMOJIS_STATIC_MORE_EMOJI,
	MAX_GUILD_INVITES,
	MAX_GUILD_MEMBERS,
	MAX_GUILD_ROLES,
	MAX_GUILD_STICKERS,
	MAX_GUILD_STICKERS_MORE_STICKERS,
	MAX_MESSAGE_LENGTH_NON_PREMIUM,
	MAX_MESSAGE_LENGTH_PREMIUM,
	MAX_PRIVATE_CHANNELS_PER_USER,
	MAX_REACTIONS_PER_MESSAGE,
	MAX_RELATIONSHIPS,
	MAX_USERS_PER_MESSAGE_REACTION,
	MAX_VOICE_MESSAGE_DURATION,
	MAX_WEBHOOKS_PER_CHANNEL,
	MAX_WEBHOOKS_PER_GUILD,
	STICKER_MAX_SIZE,
} from '@fluxer/constants/src/LimitConstants';

export const DEFAULT_RESTRICTED_LIMITS: Record<LimitKey, number> = {
	max_guild_video_fps: 30,
	max_guild_video_height: 720,
	max_guild_soundboard_sounds: 8,
	feature_role_gradient: 0,
	feature_role_icons: 0,
	feature_announcement_channels: 0,
	feature_guild_tags: 0,
	feature_guild_notification_sound: 0,
	feature_guild_wallpaper: 0,
	// TEMPORARY: delivery wakes every following guild's gateway process, and an idle guild woken without a session may
	// stay in memory. Trigger: raising this above 10 000 for any guild, or a gateway memory alert. Path: read the target
	// from storage during delivery and dispatch through a new guild.dispatch_if_running.
	max_followers_per_announcement_channel: MAX_FOLLOWERS_PER_ANNOUNCEMENT_CHANNEL,
	avatar_max_size: AVATAR_MAX_SIZE,
	emoji_max_size: EMOJI_MAX_SIZE,
	feature_animated_avatar: 0,
	feature_animated_banner: 0,
	feature_app_commands: 1,
	feature_bot_interactions: 1,
	feature_bot_components: 1,
	feature_bot_select_menus: 1,
	feature_bot_modals: 1,
	feature_bot_components_v2: 0,
	feature_bot_interactions_blocked: 0,
	feature_custom_discriminator: 0,
	feature_custom_notification_sounds: 1,
	feature_early_access: 0,
	feature_feed: 1,
	feature_feed_categories: 0,
	feature_feed_discover: 1,
	feature_feed_discover_media: 0,
	feature_feed_discover_video: 0,
	feature_feed_new: 0,
	feature_feed_post_like_notifications: 0,
	feature_feed_reply_notifications: 0,
	feature_feed_comment_like_notifications: 0,
	feature_feed_suggestions: 0,
	feature_feed_scheduled: 0,
	feature_feed_topics: 0,
	feature_feed_video: 0,
	feature_feed_views: 0,
	feature_guild_roster: 0,
	feature_mature_content_settings: 0,
	feature_micupbot: 0,
	feature_name_style: 0,
	feature_profile_theme: 0,
	feature_shop_boost_payriff: 0,
	feature_shop_boost_subscription: 0,
	feature_global_expressions: 0,
	feature_guild_create: 0,
	feature_higher_video_quality: 0,
	feature_per_guild_profiles: 0,
	feature_table_game_chance_rewards: 0,
	feature_table_game_ranked: 0,
	feature_voice_entrance_sounds: 0,
	feed_fanout_max_followers: 10_000,
	feed_fanout_max_posts_per_day: 10,
	feed_fanout_min_followers: 8000,
	feed_suggestions_min_followers: 2,
	max_attachment_file_size: ATTACHMENT_MAX_SIZE_NON_PREMIUM,
	max_attachments_per_message: MAX_ATTACHMENTS_PER_MESSAGE,
	max_bio_length: MAX_BIO_LENGTH,
	max_bookmarks: MAX_BOOKMARKS_NON_PREMIUM,
	max_boost_slots: 0,
	max_channels_per_category: MAX_CHANNELS_PER_CATEGORY,
	// TEMPORARY: a post's comments of one ten day bucket share a partition (feed_comments), and a viral post gets most of
	// them in one bucket; 10,000 comments of up to 1000 characters stay under ~40 MB. Trigger: posts reaching the cap
	// every day, or an instance setting it above 20,000 (over ~100 MB). Path: split a full bucket into numbered parts
	// (a part column in the clustering key read newest part first), keeping one partition per comment page.
	max_comments_per_post: 10_000,
	max_custom_backgrounds: 1,
	max_embeds_per_message: MAX_EMBEDS_PER_MESSAGE,
	max_favorite_meme_tags: MAX_FAVORITE_MEME_TAGS,
	max_favorite_memes: MAX_FAVORITE_MEMES_NON_PREMIUM,
	max_feed_posts_per_day: 50,
	max_feed_scheduled_posts: 25,
	max_feed_image_size: FEED_IMAGE_DEFAULT_MAX_BYTES,
	max_feed_video_size: FEED_VIDEO_DEFAULT_MAX_BYTES,
	// 50 posts of 500 MiB stay under it: only an account with a larger allowance reaches it.
	max_feed_video_bytes_per_day: FEED_VIDEO_BYTES_PER_DAY_HARD_MAX,
	max_feed_video_duration: FEED_VIDEO_DEFAULT_MAX_DURATION_SECONDS,
	max_following: 2000,
	max_group_dm_recipients: MAX_GROUP_DM_RECIPIENTS,
	max_group_dms_per_user: MAX_GROUP_DMS_PER_USER,
	max_guild_channels: MAX_GUILD_CHANNELS,
	max_guild_emojis: MAX_GUILD_EMOJIS,
	max_guild_emojis_animated_more: MAX_GUILD_EMOJIS_ANIMATED_MORE_EMOJI,
	max_guild_emojis_animated: MAX_GUILD_EMOJIS_ANIMATED,
	max_guild_emojis_static_more: MAX_GUILD_EMOJIS_STATIC_MORE_EMOJI,
	max_guild_emojis_static: MAX_GUILD_EMOJIS_STATIC,
	max_guild_invites: MAX_GUILD_INVITES,
	max_guild_members: MAX_GUILD_MEMBERS,
	max_guild_roles: MAX_GUILD_ROLES,
	max_guild_stickers_more: MAX_GUILD_STICKERS_MORE_STICKERS,
	max_guild_stickers: MAX_GUILD_STICKERS,
	max_guilds: 100,
	max_message_length: MAX_MESSAGE_LENGTH_NON_PREMIUM,
	max_private_channels_per_user: MAX_PRIVATE_CHANNELS_PER_USER,
	// TEMPORARY: bounds the bought slots one account can spend on guild_boosts partitions (the hottest boost table)
	// and keeps user_boost_slots read whole. Trigger: accounts reaching the cap, or a user_boost_slots partition over 200
	// rows. Path: page user_boost_slots and raise the cap per rule in the admin limit panel.
	max_purchased_boost_slots: 20,
	max_reactions_per_message: MAX_REACTIONS_PER_MESSAGE,
	max_relationships: MAX_RELATIONSHIPS,
	max_users_per_message_reaction: MAX_USERS_PER_MESSAGE_REACTION,
	max_voice_message_duration: MAX_VOICE_MESSAGE_DURATION,
	max_webhooks_per_channel: MAX_WEBHOOKS_PER_CHANNEL,
	max_webhooks_per_guild: MAX_WEBHOOKS_PER_GUILD,
	sticker_max_size: STICKER_MAX_SIZE,
	max_new_dms_per_hour: 25,
	max_new_dms_per_day: 200,
	max_new_dms_per_hour_new_account: 10,
	max_new_dms_per_day_new_account: 50,
	max_new_dms_per_hour_bot: 60,
	max_new_dms_per_day_bot: 500,
	new_account_age_hours: 168,
	new_dm_captcha_after: 5,
	spam_short_window_minutes: 10,
	dm_duplicate_freeze_after: 20,
	dm_open_alert_per_hour: 100,
	max_guild_joins_per_hour_new_account: 5,
	max_guild_joins_per_day_new_account: 15,
	cross_channel_spam_block_after: 5,
	cross_channel_spam_freeze_after: 10,
	cross_channel_spam_block_after_bot: 25,
};

export const DEFAULT_STOCK_LIMITS: Record<LimitKey, number> = {
	...DEFAULT_RESTRICTED_LIMITS,
	feature_animated_avatar: 1,
	feature_animated_banner: 1,
	feature_custom_discriminator: 1,
	feature_custom_notification_sounds: 1,
	feature_early_access: 1,
	feature_global_expressions: 1,
	feature_higher_video_quality: 1,
	feature_name_style: 1,
	feature_per_guild_profiles: 1,
	feature_profile_theme: 1,
	feature_voice_entrance_sounds: 1,
	max_attachment_file_size: ATTACHMENT_MAX_SIZE_PREMIUM,
	max_bio_length: MAX_BIO_LENGTH,
	max_bookmarks: MAX_BOOKMARKS_PREMIUM,
	max_boost_slots: 2,
	max_custom_backgrounds: 15,
	max_favorite_memes: MAX_FAVORITE_MEMES_PREMIUM,
	max_guilds: 200,
	max_message_length: MAX_MESSAGE_LENGTH_PREMIUM,
};

export const DEFAULT_FREE_LIMITS = DEFAULT_RESTRICTED_LIMITS;
export const DEFAULT_PREMIUM_LIMITS = DEFAULT_STOCK_LIMITS;

export const DM_BULK_TRUSTED_TRAIT = 'dm_toplu_guvenilir';

// Trait rules take the larger value, so "off" for the freeze keys is a value no sender reaches.
export const DM_BULK_TRUSTED_LIMITS: Partial<Record<LimitKey, number>> = {
	max_new_dms_per_hour: 200,
	max_new_dms_per_day: 2000,
	max_new_dms_per_hour_new_account: 200,
	max_new_dms_per_day_new_account: 2000,
	max_new_dms_per_hour_bot: 1_000_000,
	max_new_dms_per_day_bot: 1_000_000,
	new_dm_captcha_after: 200,
	dm_duplicate_freeze_after: 1_000_000,
	cross_channel_spam_block_after: 50,
	cross_channel_spam_freeze_after: 1_000_000,
	cross_channel_spam_block_after_bot: 1_000_000,
};

// Micup akis-v3: derived on the feed upload paths from the content creator badge (FeedDiscoverConfig.creator_badge_id);
// an admin cannot assign it by hand. Its rule ships with the defaults and stays editable in Limit Config.
export const FEED_CREATOR_TRAIT = 'feed_creator';

export const FEED_CREATOR_LIMITS: Partial<Record<LimitKey, number>> = {
	max_feed_posts_per_day: FEED_POSTS_PER_DAY_HARD_MAX,
	max_feed_video_size: FEED_VIDEO_HARD_MAX_BYTES,
	max_feed_video_duration: FEED_VIDEO_CREATOR_MAX_DURATION_SECONDS,
	max_feed_image_size: FEED_IMAGE_HARD_MAX_BYTES,
};
